import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconArrowBackUp, IconDeviceFloppy } from '@tabler/icons-react';
import { Button, Card, Center, Loader } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import PageHeader from '../../../../components/core/PageHeader/PageHeader';
import PageContent from '../../../../components/core/PageContent/PageContent';
import equipmentsEquipment from '../../../../services/core/equipment.service';
import { FormView, FormViewData } from './components/FormView';
import { EquipmentType } from '../../../../models/core/equipment.type';
import { Feature } from '../../../../utils/constants.utils';
import { Permission } from '../../../../models/core/departments.type';

type DataResult = {
  loading: boolean;
  equipmentData: EquipmentType | null;
};

function EquipmentAddEdit() {
  const navigate = useNavigate();
  const location: any = useLocation();
  const { idResiduoEquipamento } = useParams();

  const [dataResult, setDataResult] = useState<DataResult>({
    loading: (!!idResiduoEquipamento && !location?.state?.equipment) || false,
    equipmentData: location?.state?.equipment || null,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      let { equipmentData } = dataResult;
      try {
        equipmentData = await equipmentsEquipment.select({
          idResiduoEquipamento: Number(idResiduoEquipamento),
        });
      } catch (error: any) {
        showNotification({
          title: `Equipamento - ${!idResiduoEquipamento ? 'Adicionar' : 'Editar'}`,
          message: error?.isBusinessException
            ? error.description
            : 'Não foi possível carregar o equipamento.',
          color: 'red',
        });
        navigate(`/${error?.statusCode || '500'}`, {
          replace: true,
        });
      } finally {
        setDataResult({ loading: false, equipmentData });
      }
    };

    if (idResiduoEquipamento && dataResult.equipmentData === null) {
      fetchData();
    }
  }, [idResiduoEquipamento, navigate, dataResult]);

  const save = async (inputFormViewData: FormViewData) => {
    try {
      setSaving(true);
      let newModel;
      if (!idResiduoEquipamento) {
        newModel = await equipmentsEquipment.insert({
          ...inputFormViewData,
        });
      } else {
        await equipmentsEquipment.update({
          ...inputFormViewData,
          idResiduoEquipamento: Number(idResiduoEquipamento),
        });
      }
      showNotification({
        title: `Equipamento - ${!idResiduoEquipamento ? 'Adicionar' : 'Editar'}`,
        message: 'Equipamento salvo com sucesso.',
        color: 'green',
      });
      navigate(`/equipment/${newModel?.idResiduoEquipamento || idResiduoEquipamento}`, {
        state: {
          equipment: newModel || null,
        },
      });
    } catch (error: any) {
      showNotification({
        title: `Equipamento - ${!idResiduoEquipamento ? 'Adicionar' : 'Editar'}`,
        message: error?.isBusinessException ? error.description : 'Não foi possível salvar o equipamento.',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <PageHeader
        feature={Feature.Reference.Equipment}
        title={`Equipamento - ${!idResiduoEquipamento ? 'Adicionar' : 'Editar'}`}
        description={`${!idResiduoEquipamento ? 'Adicione' : 'Edite'} um equipamento.`}
        buttons={[
          <Button
            key="Voltar"
            color="secondary"
            leftSection={<IconArrowBackUp size={18} />}
            onClick={() => {
              navigate(-1);
            }}
            disabled={dataResult.loading || saving}
          >
            Voltar
          </Button>,
          <Button
            key="Salvar"
            color="primary"
            type="submit"
            form="equipment-add-edit"
            leftSection={<IconDeviceFloppy size={18} />}
            disabled={dataResult.loading}
            loading={saving}
            data-permission={!idResiduoEquipamento ? Permission.EquipmentAdd : Permission.EquipmentEdit}
          >
            Salvar
          </Button>,
        ]}
      />
      {idResiduoEquipamento && dataResult.equipmentData === null ? (
        <Center>
          <Loader size="xl" />
        </Center>
      ) : (
        <PageContent>
          <FormView equipment={dataResult.equipmentData} loading={saving} save={save} />
        </PageContent>
      )}
    </Card>
  );
}

export default EquipmentAddEdit;
